import React, { useMemo } from "react";
import type { All8PageContent } from "@apa/core-renderers";
import { LoadingSpinner } from "../LoadingSpinner";
import PageConnectionsModal from "../PageConnectionsModal";
import PageEditorPreviewPanel, { type PreviewViewportKey } from "./PageEditorPreviewPanel";
import PageEditorMetadataPanel from "./PageEditorMetadataPanel";
import PageEditorLayoutPanel from "./PageEditorLayoutPanel";
import PageEditorComponentsPanel from "./PageEditorComponentsPanel";
import PageEditorJsonPanel from "./PageEditorJsonPanel";
import PageEditorMarkdownPanel from "./PageEditorMarkdownPanel";
import PageEditorComponentLibraryModal from "./PageEditorComponentLibraryModal";
import PageEditorStickyActions from "./PageEditorStickyActions";
import PageTreePanel from "../PageTree/PageTreePanel";
import type { AdminPage } from "./types";
import type { ComponentDefinition } from "../../Pages/pageComponentLibrary";
import type { APAADMINBaseModelsPageTreeNode } from "../../API/Models";

type LibraryView = "grid" | "list" | "visual";

export type ContentViewMode = "blocks" | "json" | "markdown";

export type PageEditorLayoutProps = {
	editable: boolean;
	error: string | null;
	message: string | null;
	/** Shown when the draft has unsaved edits */
	unsavedChangesMessage?: string | null;
	/** Shown when save is disabled (e.g. "Slug is missing.") */
	saveBlockerReasons?: string[];
	/** Shown when saved changes are not yet deployed to preview or production */
	pendingDeployMessage?: string | null;
	loadingView: boolean;
	draft: AdminPage | null;
	contentViewMode: ContentViewMode;
	isJsonLocked: boolean;
	pageJsonError: string | null;
	onSelectBlocks: () => void;
	onSelectJson: () => void;
	onSelectMarkdown: () => void;
	/** Markdown source when view is markdown; edits round-trip to draft. Invalid content shows error but does not lock editing. */
	markdownDraft: string;
	markdownError: string | null;
	onMarkdownChange: (value: string) => void;
	onShowConnections: () => void;
	connectionsDisabled: boolean;
	metaOpen: boolean;
	setMetaOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setDraft: React.Dispatch<React.SetStateAction<AdminPage | null>>;
	resolvedPath: string;
	keywordsToInput: (keywords?: string[] | null) => string;
	inputToKeywords: (value: string) => string[];
	canChangeSlug?: boolean;
	onOpenSlugChange?: () => void;
	dynamicPreviewLabel?: string | null;
	onOpenDynamicPreview?: () => void;
	onClearDynamicPreview?: () => void;
	allowImageVariables?: boolean;
	previewViewport: PreviewViewportKey;
	setPreviewViewport: (viewport: PreviewViewportKey) => void;
	/** URL to open in new tab for preview site (empty = hide button) */
	previewPageUrl: string;
	/** URL to open in new tab for production site (empty = hide button) */
	productionPageUrl: string;
	previewContainerRef: React.RefObject<HTMLDivElement>;
	previewCss: string;
	editorComponents: All8PageContent;
	previewComponents: All8PageContent;
	previewWidth: number | null;
	previewContainerWidth: number | null;
	runtimeConfig: any;
	activeComponentKey: string | null;
	setActiveComponentKey: React.Dispatch<React.SetStateAction<string | null>>;
	updateComponentValue: (index: number, path: Array<string | number>, value: any) => void;
	removeComponent: (index: number) => void;
	moveComponent: (from: number, to: number) => void;
	jsonDrafts: Record<string, string>;
	setJsonDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	jsonErrors: Record<string, string>;
	setJsonErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	showLibrary: boolean;
	setShowLibrary: React.Dispatch<React.SetStateAction<boolean>>;
	libraryView: LibraryView;
	setLibraryView: (view: LibraryView) => void;
	componentSearch: string;
	setComponentSearch: (value: string) => void;
	filteredLibrary: ComponentDefinition[];
	availableTags: string[];
	activeTags: string[];
	onToggleTag: (tag: string) => void;
	onClearTags: () => void;
	componentDefinitionMap: Map<string, ComponentDefinition>;
	librarySelection: ComponentDefinition | null;
	setLibrarySelection: (definition: ComponentDefinition | null) => void;
	addComponentFromDefinition: (definition: ComponentDefinition | null) => void;
	/** When in markdown view, modal selection inserts at cursor via this ref. */
	markdownInsertRef: React.MutableRefObject<((def: ComponentDefinition) => void) | null>;
	pageJsonDraft: string;
	handleFormatJson: () => void;
	handlePageJsonChange: (value: string) => void;
	jsonEditorOptions: any;
	onDeployPreview: () => void;
	deployPreviewDisabled: boolean;
	deployingPreview: boolean;
	previewState: "up-to-date" | "needs-deploy" | "pending";
	previewDeployedAt?: string | null;
	productionState: "up-to-date" | "needs-deploy" | "pending";
	productionDeployedAt?: string | null;
	onGoToDeployment: () => void;
	onSelectPageFromTree: (node: APAADMINBaseModelsPageTreeNode) => void;
	connectionsOpen: boolean;
	setConnectionsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	initialLanguage: string;
	/** Page tree (left nav) visible; toggle to hide/show. */
	pageNavOpen: boolean;
	setPageNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** Page is soft-deleted; show red banner and Restore button. */
	isPageDeleted?: boolean;
	onRestore?: () => void | Promise<void>;
	restoring?: boolean;
};

/** Main page editor layout with navigation tree, editing panel and preview. */
const PageEditorLayout: React.FC<PageEditorLayoutProps> = ({
	editable,
	error,
	message,
	unsavedChangesMessage,
	saveBlockerReasons = [],
	pendingDeployMessage,
	loadingView,
	draft,
	contentViewMode,
	isJsonLocked,
	pageJsonError,
	onSelectBlocks,
	onSelectJson,
	onSelectMarkdown,
	markdownDraft,
	markdownError,
	onMarkdownChange,
	onShowConnections,
	connectionsDisabled,
	metaOpen,
	setMetaOpen,
	setDraft,
	resolvedPath,
	keywordsToInput,
	inputToKeywords,
	canChangeSlug,
	onOpenSlugChange,
	dynamicPreviewLabel,
	onOpenDynamicPreview,
	onClearDynamicPreview,
	allowImageVariables,
	previewViewport,
	setPreviewViewport,
	previewPageUrl,
	productionPageUrl,
	previewContainerRef,
	previewCss,
	editorComponents,
	previewComponents,
	previewWidth,
	previewContainerWidth,
	runtimeConfig,
	activeComponentKey,
	setActiveComponentKey,
	updateComponentValue,
	removeComponent,
	moveComponent,
	jsonDrafts,
	setJsonDrafts,
	jsonErrors,
	setJsonErrors,
	showLibrary,
	setShowLibrary,
	libraryView,
	setLibraryView,
	componentSearch,
	setComponentSearch,
	filteredLibrary,
	availableTags,
	activeTags,
	onToggleTag,
	onClearTags,
	componentDefinitionMap,
	librarySelection,
	setLibrarySelection,
	addComponentFromDefinition,
	markdownInsertRef,
	pageJsonDraft,
	handleFormatJson,
	handlePageJsonChange,
	jsonEditorOptions,
	onDeployPreview,
	deployPreviewDisabled,
	deployingPreview,
	previewState,
	previewDeployedAt,
	productionState,
	productionDeployedAt,
	onGoToDeployment,
	onSelectPageFromTree,
	connectionsOpen,
	setConnectionsOpen,
	initialLanguage,
	pageNavOpen,
	setPageNavOpen,
	isPageDeleted,
	onRestore,
	restoring
}) => {
	const normalizedSlug = useMemo(() => (draft?.slug ?? "").trim().replace(/^\/+|\/+$/g, ""), [draft?.slug]);
	const expandedPaths = useMemo(() => {
		if (!normalizedSlug) return [];
		const segments = normalizedSlug.split("/").filter(Boolean);
		return segments.slice(0, -1).map((_, index) => segments.slice(0, index + 1).join("/"));
	}, [normalizedSlug]);

	return (
		<div className="w-full h-full flex flex-col overflow-hidden">
			<div className="px-4 sm:px-6 pt-4 pb-3 space-y-3 shrink-0">
				{isPageDeleted && (
					<div
						className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-rose-300 bg-rose-100 px-4 py-3 text-sm font-medium text-rose-800"
						role="alert"
					>
						<span>This page is deleted. Restore it to publish to preview again.</span>
						{onRestore && (
							<button
								type="button"
								onClick={() => onRestore()}
								disabled={restoring}
								className="inline-flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-rose-700 disabled:opacity-60"
							>
								{restoring ? (
									<>
										<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
										Restoring…
									</>
								) : (
									<>Restore page</>
								)}
							</button>
						)}
					</div>
				)}
				{unsavedChangesMessage && (
					<div
						className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800"
						role="status"
					>
						<span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
						{unsavedChangesMessage}
					</div>
				)}
				{saveBlockerReasons.length > 0 && (
					<div
						className="flex flex-col gap-1 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-medium text-rose-700"
						role="alert"
					>
						{saveBlockerReasons.map((reason) => (
							<span key={reason}>{reason}</span>
						))}
					</div>
				)}
				{pendingDeployMessage && (
					<div
						className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800"
						role="status"
					>
						<span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
						{pendingDeployMessage}
					</div>
				)}
				{error && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
						{error}
					</div>
				)}
				{message && (
					<div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
						{message}
					</div>
				)}
			</div>

			<div className="flex-1 min-h-0 px-4 sm:px-6 pb-6">
				{loadingView && !draft ? (
					<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-slate-500">
						<LoadingSpinner size="lg" />
						<span className="ml-2 inline-block align-middle">Loading page…</span>
					</div>
				) : (
					<div className="flex h-full gap-5">
						{pageNavOpen && (
							<PageTreePanel
								className="w-[350px] shrink-0 h-full"
								language={draft?.language ?? initialLanguage}
								activePageId={draft?.id ?? null}
								activePageSlug={draft?.slug ?? null}
								defaultExpandedPaths={expandedPaths}
								onSelectPage={onSelectPageFromTree}
							/>
						)}
						<div className="flex-1 min-w-0 h-full flex flex-col gap-4">
							<PageEditorStickyActions
								contentViewMode={contentViewMode}
								isJsonLocked={isJsonLocked}
								pageJsonError={pageJsonError}
								onSelectBlocks={onSelectBlocks}
								onSelectJson={onSelectJson}
								onSelectMarkdown={onSelectMarkdown}
								onShowConnections={onShowConnections}
								connectionsDisabled={connectionsDisabled}
								onDeployPreview={onDeployPreview}
								deployPreviewDisabled={deployPreviewDisabled}
								deployingPreview={deployingPreview}
								previewState={previewState}
								previewDeployedAt={previewDeployedAt}
								productionState={productionState}
								productionDeployedAt={productionDeployedAt}
								onGoToDeployment={onGoToDeployment}
								pageNavOpen={pageNavOpen}
								onTogglePageNav={() => setPageNavOpen((open) => !open)}
							/>
							<div className="flex-1 min-h-0 grid gap-5 md:grid-cols-2">
								<div className="min-h-0 min-w-0 overflow-hidden">
									{contentViewMode === "blocks" ? (
										<div className="h-full min-h-0 overflow-auto space-y-4 pr-1">
											<PageEditorMetadataPanel
												editable={editable}
												draft={draft}
												metaOpen={metaOpen}
												onToggleMeta={() => setMetaOpen((open) => !open)}
												setDraft={setDraft}
												resolvedPath={resolvedPath}
												keywordsToInput={keywordsToInput}
												inputToKeywords={inputToKeywords}
												canChangeSlug={canChangeSlug}
												onOpenSlugChange={onOpenSlugChange}
											/>
											<PageEditorLayoutPanel
												editable={editable}
												value={draft?.layout ?? null}
												onChange={(next) =>
													setDraft((prev) => (prev ? { ...prev, layout: next } : prev))
												}
											/>
											<PageEditorComponentsPanel
												editable={editable}
												previewComponents={editorComponents}
												componentDefinitionMap={componentDefinitionMap}
												activeComponentKey={activeComponentKey}
												setActiveComponentKey={setActiveComponentKey}
												updateComponentValue={updateComponentValue}
												removeComponent={removeComponent}
												moveComponent={moveComponent}
												jsonDrafts={jsonDrafts}
												setJsonDrafts={setJsonDrafts}
												jsonErrors={jsonErrors}
												setJsonErrors={setJsonErrors}
												onOpenLibrary={() => setShowLibrary(true)}
												onAddPageHeader={() => addComponentFromDefinition(componentDefinitionMap.get("PageHeader") ?? null)}
												allowImageVariables={allowImageVariables}
											/>
										</div>
									) : contentViewMode === "markdown" ? (
										<PageEditorMarkdownPanel
											editable={editable}
											value={markdownDraft}
											error={markdownError}
											onChange={onMarkdownChange}
											editorOptions={jsonEditorOptions}
											componentDefinitionMap={componentDefinitionMap}
											pageTitle={(() => {
												const t = (draft?.title ?? "").trim();
												if (t) return t;
												const first = draft?.components?.[0];
												if (first?.content != null && typeof first.content === "object") {
													const ct = String((first.content as { title?: string }).title ?? "").trim();
													if (ct) return ct;
												}
												return (draft?.slug ?? "").trim() || "";
											})()}
											pageDescription={draft?.metadata?.description ?? undefined}
											onOpenComponentLibrary={() => setShowLibrary(true)}
											insertComponentRef={markdownInsertRef}
										/>
									) : (
										<PageEditorJsonPanel
											editable={editable}
											pageJsonDraft={pageJsonDraft}
											pageJsonError={pageJsonError}
											onFormat={handleFormatJson}
											onChange={handlePageJsonChange}
											jsonEditorOptions={jsonEditorOptions}
										/>
									)}
								</div>

								<div className="min-h-0 min-w-0 overflow-hidden">
									<PageEditorPreviewPanel
										previewViewport={previewViewport}
										onViewportChange={setPreviewViewport}
										pageId={draft?.id ?? null}
										pageTitle={draft?.title ?? draft?.slug ?? null}
										previewPageUrl={previewPageUrl}
										productionPageUrl={productionPageUrl}
										previewContainerRef={previewContainerRef}
										previewCss={previewCss}
										previewComponents={previewComponents}
										previewWidth={previewWidth}
										previewContainerWidth={previewContainerWidth}
										runtimeConfig={runtimeConfig}
										dynamicPreviewLabel={dynamicPreviewLabel}
										onOpenDynamicPreview={onOpenDynamicPreview}
										onClearDynamicPreview={onClearDynamicPreview}
										minHeight={450}
									/>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

		<PageEditorComponentLibraryModal
			show={showLibrary}
			onClose={() => setShowLibrary(false)}
			libraryView={libraryView}
			setLibraryView={setLibraryView}
			componentSearch={componentSearch}
			setComponentSearch={setComponentSearch}
			filteredLibrary={filteredLibrary}
			availableTags={availableTags}
			activeTags={activeTags}
			onToggleTag={onToggleTag}
			onClearTags={onClearTags}
			librarySelection={librarySelection}
			setLibrarySelection={setLibrarySelection}
			addComponentFromDefinition={addComponentFromDefinition}
			runtimeConfig={runtimeConfig}
		/>

		<PageConnectionsModal
			open={connectionsOpen}
			onClose={() => setConnectionsOpen(false)}
			pageId={draft?.id ?? null}
			slug={draft?.slug ?? null}
			language={draft?.language ?? initialLanguage}
		/>
		</div>
	);
};

export default PageEditorLayout;
